import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useDisplay } from 'vuetify';
import { getOrientation } from '../util/ui';

export const FONT_SIZE = 1.7;
export const MOBILE_FONT_SIZE = 1.2;
export const MOBILE_LANDSCAPE_FONT_SIZE = 1.1;

export const MIN_PLAYBACK_RATE = 0.5;
export const MAX_PLAYBACK_RATE = 2.0;

type SettingState = {
  audioTrackingAvailable: boolean;
  audioNarrationDisabled: boolean;
  autoPageTurnAvailable: boolean;
  narrationSpeedChangeAvailable: boolean;
  isPageTurnAnimationEnabled: boolean;
  isPageSlideAnimationEnabled: boolean;
  isReflowablePageSlideAnimationEnabled: boolean;
  touchControlsEnabled: boolean;
};

const defaultPlaybackRate = 1;
const defaultVolume = 1.0;
const defaultSettingState: SettingState = {
  audioTrackingAvailable: true,
  audioNarrationDisabled: false,
  autoPageTurnAvailable: true,
  narrationSpeedChangeAvailable: true,
  isPageTurnAnimationEnabled: false,
  isPageSlideAnimationEnabled: false,
  isReflowablePageSlideAnimationEnabled: false,
  touchControlsEnabled: false,
};

const useSettingStore = defineStore(
  'book-player-setting',
  () => {
    const settingState = ref<SettingState>(defaultSettingState);

    const { smAndDown: isMobile } = useDisplay();

    const deviceId = ref<string | null>(null);

    const _playbackRate = ref(defaultPlaybackRate);

    const playbackRate = computed({
      get: () => _playbackRate.value,
      set: (newValue) => {
        _playbackRate.value = Math.min(Math.max(newValue, MIN_PLAYBACK_RATE), MAX_PLAYBACK_RATE);
      },
    });

    const audioVolume = ref(defaultVolume);
    const muted = ref(false);
    const audioTracking = ref(true);
    const autoPageTurn = ref(true);
    const darkTheme = ref(false);
    const dyslexicFont = ref(false);
    const skipBy = ref<string>('default');
    const textClickPlaybackEnabled = ref(true);
    const pageDisplayMode = ref<'single' | 'spread'>('spread');
    const autoFollowPlayback = ref(false);

    // Audio highlight appearance settings
    const audioHighlightColor = ref('#fde68a');
    const audioHighlightOpacity = ref('0.6');
    const audioHighlightBlendMode = ref('multiply');

    const mobileFontSize = computed(() =>
      getOrientation() === 'landscape' ? MOBILE_LANDSCAPE_FONT_SIZE : MOBILE_FONT_SIZE,
    );
    const baseFontSize = ref<number>(isMobile.value ? mobileFontSize.value : FONT_SIZE);
    const fontSize = ref<number>(baseFontSize.value);

    const audioTrackingAvailable = computed(() => settingState.value.audioTrackingAvailable);
    const hasAudioTracking = computed(() => audioTracking.value);
    const isDarkTheme = computed(() => darkTheme.value);
    const hasAutoPageTurn = computed(() => autoPageTurn.value);
    const isMuted = computed(() => muted.value);
    const isAudioNarrationDisabled = computed(() => settingState.value.audioNarrationDisabled);
    const isAutoPageTurnAvailable = computed(() => settingState.value.autoPageTurnAvailable);
    const isNarrationSpeedChangeAvailable = computed(() => settingState.value.narrationSpeedChangeAvailable);
    const isPageTurnAnimationEnabled = computed(() => settingState.value.isPageTurnAnimationEnabled);
    const isPageSlideAnimationEnabled = computed(() => settingState.value.isPageSlideAnimationEnabled);
    const isReflowablePageSlideAnimationEnabled = computed(
      () => settingState.value.isReflowablePageSlideAnimationEnabled,
    );
    const isTouchControlsEnabled = computed(() => settingState.value.touchControlsEnabled);
    const isTextClickPlaybackEnabled = computed(() => textClickPlaybackEnabled.value);
    const isPageDisplayModeSingle = computed(() => pageDisplayMode.value === 'single');
    const isAutoFollowPlayback = computed(() => autoFollowPlayback.value);

    watch(audioVolume, () => {
      // Volume must be between 0.0 and 1.0
      audioVolume.value = Math.max(0.0, Math.min(defaultVolume, audioVolume.value));

      muted.value = audioVolume.value === 0.0;
    });

    const setDeviceId = () => {
      if (!deviceId.value) {
        deviceId.value = uuidv4();
      }
    };

    const setAudioTrackingAvailable = (_audioTrackingAvailable: boolean) => {
      settingState.value.audioTrackingAvailable = _audioTrackingAvailable;
      if (!settingState.value.audioTrackingAvailable) {
        audioTracking.value = _audioTrackingAvailable;
      }
    };

    const setAudioNarrationDisabled = (value: boolean) => {
      settingState.value.audioNarrationDisabled = value;
      if (value) {
        audioVolume.value = 0;
        muted.value = true;
      } else {
        // Re-enable audio when narration is enabled
        audioVolume.value = defaultVolume;
        muted.value = false;
      }
    };

    const setAutoPageTurnAvailable = (value: boolean) => {
      settingState.value.autoPageTurnAvailable = value;
      autoPageTurn.value = value;
    };

    const setNarrationSpeedChangeAvailable = (value: boolean) => {
      settingState.value.narrationSpeedChangeAvailable = value;
      if (!settingState.value.narrationSpeedChangeAvailable) {
        playbackRate.value = defaultPlaybackRate;
      }
    };

    const setIsPageTurnAnimationEnabled = (value: boolean) => {
      settingState.value.isPageTurnAnimationEnabled = value;
    };

    const setIsPageSlideAnimationEnabled = (value: boolean) => {
      settingState.value.isPageSlideAnimationEnabled = value;
    };

    const setIsReflowablePageSlideAnimationEnabled = (value: boolean) => {
      settingState.value.isReflowablePageSlideAnimationEnabled = value;
    };

    const $reset = () => {
      playbackRate.value = defaultPlaybackRate;
      audioVolume.value = defaultVolume;
      muted.value = false;
      audioTracking.value = true;
      autoPageTurn.value = true;
      darkTheme.value = false;
      fontSize.value = isMobile.value ? mobileFontSize.value : FONT_SIZE;
      dyslexicFont.value = false;
      skipBy.value = 'default';
      textClickPlaybackEnabled.value = true;
      pageDisplayMode.value = 'spread';
      autoFollowPlayback.value = false;
      // Reset audio highlight to theme-appropriate defaults
      // Since darkTheme is being reset to false, always use light mode defaults
      audioHighlightColor.value = '#fde68a';
      audioHighlightOpacity.value = '0.6';
      audioHighlightBlendMode.value = 'multiply';
    };

    const resetState = () => {
      $reset();
    };

    const partialReset = () => {
      // Only reset settings that are not persisted
      settingState.value = defaultSettingState;
      audioVolume.value = defaultVolume;
      muted.value = false;
      darkTheme.value = false;
      textClickPlaybackEnabled.value = true;
      autoFollowPlayback.value = false;
    };

    const setAutomaticPageTurnByDefault = (defaultAutoPageTurn: boolean) => {
      autoPageTurn.value = defaultAutoPageTurn;
    };

    const setIsTouchControlsEnabled = (value: boolean) => {
      settingState.value.touchControlsEnabled = value;
    };

    const setIsTextClickPlaybackEnabled = (value: boolean) => {
      textClickPlaybackEnabled.value = value;
    };

    const setAutoFollowPlayback = (value: boolean) => {
      autoFollowPlayback.value = value;
    };

    const getBookPlayerSettingsForAnalytics = () => {
      // For some reason, sometimes the playback rate object is null during the analytics client call.
      // This is a workaround the error. TODO: Figure out why it happens.
      return {
        audioVolume: audioVolume.value.toString(),
        autoPageTurn: hasAutoPageTurn.value.toString(),
        isMuted: isMuted.value.toString(),
        playbackRate: playbackRate.value?.toString() ?? 'Not specified', // For some reason, sometimes the playback rate object is null during the analytics client call. This is a workaround the error. TODO: Figure out why it happens.
        fontSize: fontSize.value.toString(),
        isAudioNarrationDisabled: isAudioNarrationDisabled.value.toString(),
        audioTrackingEnabled: audioTracking.value.toString(),
      };
    };

    return {
      settingState,
      audioVolume,
      autoPageTurn,
      hasAutoPageTurn,
      darkTheme,
      isDarkTheme,
      deviceId,
      audioTracking,
      hasAudioTracking,
      audioTrackingAvailable,
      skipBy,
      muted,
      isMuted,
      playbackRate,
      baseFontSize,
      fontSize,
      dyslexicFont,
      isAudioNarrationDisabled,
      isAutoPageTurnAvailable,
      isNarrationSpeedChangeAvailable,
      isPageTurnAnimationEnabled,
      isPageSlideAnimationEnabled,
      isReflowablePageSlideAnimationEnabled,
      isTouchControlsEnabled,
      isTextClickPlaybackEnabled,
      pageDisplayMode,
      isPageDisplayModeSingle,
      autoFollowPlayback,
      isAutoFollowPlayback,
      audioHighlightColor,
      audioHighlightOpacity,
      audioHighlightBlendMode,
      resetState,
      $reset,
      partialReset,
      setDeviceId,
      setAudioTrackingAvailable,
      setAudioNarrationDisabled,
      setAutoPageTurnAvailable,
      setNarrationSpeedChangeAvailable,
      setIsPageTurnAnimationEnabled,
      setIsPageSlideAnimationEnabled,
      setIsReflowablePageSlideAnimationEnabled,
      setAutomaticPageTurnByDefault,
      setIsTouchControlsEnabled,
      setIsTextClickPlaybackEnabled,
      setAutoFollowPlayback,
      getBookPlayerSettingsForAnalytics,
    };
  },
  {
    // @ts-ignore Part of persistence plugin
    persist: {
      pick: [
        'deviceId',
        'autoPageTurn',
        'darkTheme',
        'audioTracking',
        'skipBy',
        'playbackRate',
        'baseFontSize',
        'fontSize',
        'dyslexicFont',
        'pageDisplayMode',
        'autoFollowPlayback',
        'audioHighlightColor',
        'audioHighlightOpacity',
        'audioHighlightBlendMode',
      ],
    },
  },
);

export default useSettingStore;
